import { useState, useEffect } from "react";
import {
  RiUserAddLine,
  RiCalendarLine,
  RiUploadCloud2Line,
  RiArrowDownSLine,
  RiCheckLine,
  RiCloseLine,
  RiDeleteBinLine,
} from "@remixicon/react";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
} from "@/components/ui/Drawer";
import { apiClient } from "@/lib/api";

const ACCEPTED_FILE_TYPES = ".jpg,.jpeg,.png,.pdf";
const MAX_FILE_SIZE_MB = 10;

const SelectPassengerPopup = ({ isOpen, onClose, onSelect, passengers }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center pt-24">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl mx-4 overflow-hidden">
        <div className="flex items-center justify-between px-6 pt-6 pb-3">
          <h3 className="text-lg font-semibold text-gray-900">Select Passenger</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <RiCloseLine className="w-5 h-5" />
          </button>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {passengers.length === 0 ? (
            <p className="px-5 py-6 text-sm text-gray-400 text-center">No passengers found</p>
          ) : (
            passengers.map((p) => {
              const name = p.full_name || [p.first_name, p.last_name].filter(Boolean).join(" ");
              return (
                <button
                  key={p.client_passenger_id || p.id}
                  onClick={() => onSelect(p)}
                  className="w-full text-left px-5 py-3 hover:bg-gray-50 transition-colors flex items-center justify-between"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-900">{name}</p>
                    {p.nationality && (
                      <p className="text-xs text-gray-400">{p.nationality}</p>
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

const AddPassengerDrawer = ({ isOpen, onClose, onAdd, onRemove, editingPassenger, newOnly = false, clientId = null, asModal = false }) => {
  const [activeTab, setActiveTab] = useState("new");
  const [removing, setRemoving] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fullName, setFullName] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [nationality, setNationality] = useState("");
  const [passportFiles, setPassportFiles] = useState([]);
  const [visaFiles, setVisaFiles] = useState([]);
  const [existingPassportUrl, setExistingPassportUrl] = useState("");
  const [existingVisaUrl, setExistingVisaUrl] = useState("");
  const [clientPassengerId, setClientPassengerId] = useState(null);
  const [weight, setWeight] = useState("");
  const [weightUnit, setWeightUnit] = useState("lbs");
  const [saveForFuture, setSaveForFuture] = useState(false);
  const [additionalInfoOpen, setAdditionalInfoOpen] = useState(false);
  const [dietaryCatering, setDietaryCatering] = useState("");
  const [cabinEnvironment, setCabinEnvironment] = useState("");
  const [medicalMobility, setMedicalMobility] = useState("");
  const [petAccommodations, setPetAccommodations] = useState("");
  const [selectPopupOpen, setSelectPopupOpen] = useState(false);
  const [clients, setClients] = useState([]);

  const resetForm = () => {
    setFullName("");
    setDateOfBirth("");
    setNationality("");
    setPassportFiles([]);
    setVisaFiles([]);
    setExistingPassportUrl("");
    setExistingVisaUrl("");
    setClientPassengerId(null);
    setWeight("");
    setWeightUnit("lbs");
    setSaveForFuture(false);
    setAdditionalInfoOpen(false);
    setDietaryCatering("");
    setCabinEnvironment("");
    setMedicalMobility("");
    setPetAccommodations("");
  };

  const handleTabSwitch = (tab) => {
    if (tab === activeTab) return;
    resetForm();
    setActiveTab(tab);
  };

  // Fetch passengers/clients for "Existing" tab
  useEffect(() => {
    if (isOpen) {
      if (clientId) {
        apiClient.getClientPassengers(clientId).then((res) => {
          const list = Array.isArray(res) ? res : (res?.data || []);
          setClients(list);
        }).catch(() => setClients([]));
      } else {
        apiClient.getClients(0, 200).then((res) => {
          setClients(res?.data || res || []);
        }).catch(() => setClients([]));
      }
    }
  }, [isOpen, clientId]);

  // Reset form when drawer opens
  useEffect(() => {
    if (isOpen) {
      if (editingPassenger) {
        setFullName(editingPassenger.full_name || editingPassenger.fullName || "");
        // Convert API date (YYYY-MM-DD or YYYY-MM-DDT...) to display format (MM/DD/YYYY)
        const rawDob = editingPassenger.date_of_birth || editingPassenger.dateOfBirth || "";
        const dobMatch = rawDob.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (dobMatch) {
          setDateOfBirth(`${dobMatch[2]}/${dobMatch[3]}/${dobMatch[1]}`);
        } else {
          setDateOfBirth(rawDob);
        }
        setNationality(editingPassenger.nationality || "");
        setPassportFiles(editingPassenger.passportFiles || []);
        setVisaFiles(editingPassenger.visaFiles || []);
        setExistingPassportUrl(editingPassenger.passport_url || editingPassenger.existingPassportUrl || "");
        setExistingVisaUrl(editingPassenger.visa_url || editingPassenger.existingVisaUrl || "");
        setClientPassengerId(editingPassenger.client_passenger_id || editingPassenger.clientPassengerId || null);
        setWeight(editingPassenger.weight || "");
        setWeightUnit(editingPassenger.weightUnit || "lbs");
        const info = editingPassenger.additional_info || {};
        setDietaryCatering(info.dietary_catering || editingPassenger.dietaryCatering || "");
        setCabinEnvironment(info.cabin_environment || editingPassenger.cabinEnvironment || "");
        setMedicalMobility(info.medical_mobility || editingPassenger.medicalMobility || "");
        setPetAccommodations(info.pet_accommodations || editingPassenger.petAccommodations || "");
        setSaveForFuture(false);
      } else {
        resetForm();
      }
      setActiveTab("new");
    }
  }, [isOpen, editingPassenger]);

  const isFormValid = fullName.trim() !== "";

  const handleFileChange = (e, setter) => {
    const files = Array.from(e.target.files || []);
    const valid = files.filter(
      (f) =>
        f.size <= MAX_FILE_SIZE_MB * 1024 * 1024 &&
        /\.(jpe?g|png|pdf)$/i.test(f.name)
    );
    setter(valid);
    e.target.value = "";
  };

  const handleSubmit = async () => {
    if (!isFormValid || saving) return;
    setSaving(true);
    try {
      await onAdd({
        fullName: fullName.trim(),
        dateOfBirth: dateOfBirth.trim(),
        nationality: nationality.trim(),
        passportFiles,
        visaFiles,
        existingPassportUrl,
        existingVisaUrl,
        clientPassengerId,
        weight: Number(weight),
        weightUnit,
        dietaryCatering: dietaryCatering.trim(),
        cabinEnvironment: cabinEnvironment.trim(),
        medicalMobility: medicalMobility.trim(),
        petAccommodations: petAccommodations.trim(),
      });
    } finally {
      setSaving(false);
    }
  };

  const formContent = (
    <>
        {/* Content */}
        <div className={`flex-1 overflow-y-auto ${asModal ? 'px-5 py-4' : 'py-5'}`}>
          {/* Tab Toggle - hidden when editing or newOnly */}
          {!editingPassenger && !newOnly && (
            <div className="flex bg-gray-100 rounded-xl p-1 mb-6">
              <button
                onClick={() => handleTabSwitch("new")}
                className={`flex-1 py-1 text-sm font-medium rounded-lg transition-colors ${
                  activeTab === "new"
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                New
              </button>
              <button
                onClick={() => handleTabSwitch("existing")}
                className={`flex-1 py-1 text-sm font-medium rounded-lg transition-colors ${
                  activeTab === "existing"
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                Existing
              </button>
            </div>
          )}

          <div className="space-y-5">
              {/* Full Legal Name */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-1.5">
                  Full Legal Name<span className="text-red-500">*</span>
                </label>
                {activeTab === "existing" ? (
                  <button
                    type="button"
                    onClick={() => setSelectPopupOpen(true)}
                    className="w-full text-left text-sm border border-gray-200 rounded-lg px-3 py-2.5 outline-none focus:border-primary-400 transition-colors"
                  >
                    {fullName ? (
                      <span className="text-gray-900">{fullName}</span>
                    ) : (
                      <span className="text-gray-400">Select passenger...</span>
                    )}
                  </button>
                ) : (
                  <input
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="John Doe"
                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 outline-none focus:border-primary-400 transition-colors placeholder-gray-400"
                  />
                )}
              </div>

              {/* Date of Birth */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-1.5">
                  Date of Birth
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={dateOfBirth}
                    onChange={(e) => {
                      const raw = e.target.value.replace(/\D/g, "").slice(0, 8);
                      let formatted = "";
                      if (raw.length > 4) {
                        formatted = `${raw.slice(0, 2)}/${raw.slice(2, 4)}/${raw.slice(4)}`;
                      } else if (raw.length > 2) {
                        formatted = `${raw.slice(0, 2)}/${raw.slice(2)}`;
                      } else {
                        formatted = raw;
                      }
                      setDateOfBirth(formatted);
                    }}
                    placeholder="MM/DD/YYYY"
                    maxLength={10}
                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 outline-none focus:border-primary-400 transition-colors placeholder-gray-400"
                  />
                  <RiCalendarLine className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
              </div>

              {/* Nationality */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-1.5">
                  Nationality
                </label>
                <input
                  type="text"
                  value={nationality}
                  onChange={(e) => setNationality(e.target.value)}
                  placeholder="American"
                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 outline-none focus:border-primary-400 transition-colors placeholder-gray-400"
                />
                <p className="text-xs text-gray-400 mt-1">
                  In case of dual-citizenship, separate nationalities with a comma.
                </p>
              </div>

              {/* Passport & Visa */}
              <div className="grid grid-cols-2 gap-3">
                {/* Passport */}
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-1.5">
                    Passport
                  </label>
                  <label className="flex items-center border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <span className={`text-sm truncate flex-1 pl-3 py-2.5 ${passportFiles.length > 0 || existingPassportUrl ? 'text-gray-700' : 'text-gray-400'}`}>
                      {passportFiles.length > 0
                        ? passportFiles.map((f) => f.name).join(", ")
                        : existingPassportUrl
                          ? existingPassportUrl.split("/").pop()
                          : "Browse file"}
                    </span>
                    {(passportFiles.length > 0 || existingPassportUrl) ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setPassportFiles([]);
                          setExistingPassportUrl("");
                        }}
                        className="flex-shrink-0 flex items-center justify-center w-8 h-7 border-l border-gray-200 text-red-500 hover:text-red-600 transition-colors"
                        aria-label="Remove passport file"
                      >
                        <RiDeleteBinLine className="w-4 h-4" />
                      </button>
                    ) : (
                      <div className="flex-shrink-0 flex items-center justify-center w-8 h-7 border-l border-gray-200  transition-colorss">
                        <RiUploadCloud2Line className="w-4 h-4 text-gray-400" />
                      </div>
                    )}
                    <input
                      type="file"
                      accept={ACCEPTED_FILE_TYPES}
                      multiple
                      onChange={(e) => handleFileChange(e, setPassportFiles)}
                      className="hidden"
                    />
                  </label>
                  <p className="text-[11px] text-gray-400 mt-1 leading-tight">
                    Upload the front and back pages of passport. JPG, PNG, and PDF formats, up to 10 MB.
                  </p>
                </div>

                {/* Visa */}
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-1.5">
                    Visa
                  </label>
                  <label className="flex items-center border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <span className={`text-sm truncate flex-1 pl-3 py-2.5 ${visaFiles.length > 0 || existingVisaUrl ? 'text-gray-700' : 'text-gray-400'}`}>
                      {visaFiles.length > 0
                        ? visaFiles.map((f) => f.name).join(", ")
                        : existingVisaUrl
                          ? existingVisaUrl.split("/").pop()
                          : "Browse file"}
                    </span>
                    {(visaFiles.length > 0 || existingVisaUrl) ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setVisaFiles([]);
                          setExistingVisaUrl("");
                        }}
                        className="flex-shrink-0 flex items-center justify-center w-8 h-7 border-l border-gray-200 text-red-500 hover:text-red-600 transition-colors"
                        aria-label="Remove visa file"
                      >
                        <RiDeleteBinLine className="w-4 h-4" />
                      </button>
                    ) : (
                      <div className="flex-shrink-0 flex items-center justify-center w-8 h-7 border-l border-gray-200  transition-colors">
                        <RiUploadCloud2Line className="w-4 h-4 text-gray-400" />
                      </div>
                    )}
                    <input
                      type="file"
                      accept={ACCEPTED_FILE_TYPES}
                      multiple
                      onChange={(e) => handleFileChange(e, setVisaFiles)}
                      className="hidden"
                    />
                  </label>
                  <p className="text-[11px] text-gray-400 mt-1 leading-tight">
                    Upload passenger Visa. JPG, PNG, and PDF formats, up to 10 MB.
                  </p>
                </div>
              </div>

              {/* Weight */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-1.5">
                  Weight
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={weight}
                    onChange={(e) => setWeight(e.target.value)}
                    placeholder="Enter weight..."
                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 pr-16 outline-none focus:border-primary-400 transition-colors placeholder-gray-400"
                  />
                  <div className="absolute right-1 top-1/2 -translate-y-1/2">
                    <select
                      value={weightUnit}
                      onChange={(e) => setWeightUnit(e.target.value)}
                      className="text-sm text-gray-600 bg-transparent border-none outline-none cursor-pointer py-1 pr-1"
                    >
                      <option value="lbs">lbs</option>
                      <option value="kg">kg</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Save for future */}
              <label className="flex items-center gap-2.5 cursor-pointer">
                <div
                  onClick={() => setSaveForFuture(!saveForFuture)}
                  className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                    saveForFuture
                      ? "bg-primary-500 border-primary-500"
                      : "border-gray-300"
                  }`}
                >
                  {saveForFuture && <RiCheckLine className="w-3 h-3 text-white" />}
                </div>
                <span className="text-sm text-gray-600">Save passenger information for future use</span>
              </label>

              {/* Additional Information */}
              <div className="border border-gray-200 rounded-lg">
                <button
                  onClick={() => setAdditionalInfoOpen(!additionalInfoOpen)}
                  className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-900"
                >
                  Additional Information
                  <RiArrowDownSLine
                    className={`w-4 h-4 text-gray-400 transition-transform ${
                      additionalInfoOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {additionalInfoOpen && (
                  <div className="px-4 pb-4 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-1.5">Dietary & Catering</label>
                      <input
                        type="text"
                        value={dietaryCatering}
                        onChange={(e) => setDietaryCatering(e.target.value)}
                        placeholder="Enter preferences..."
                        className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 outline-none focus:border-primary-400 transition-colors placeholder-gray-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-1.5">Cabin Environment</label>
                      <input
                        type="text"
                        value={cabinEnvironment}
                        onChange={(e) => setCabinEnvironment(e.target.value)}
                        placeholder="Enter preferences..."
                        className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 outline-none focus:border-primary-400 transition-colors placeholder-gray-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-1.5">Medical/Mobility Requirements</label>
                      <input
                        type="text"
                        value={medicalMobility}
                        onChange={(e) => setMedicalMobility(e.target.value)}
                        placeholder="Enter requirements..."
                        className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 outline-none focus:border-primary-400 transition-colors placeholder-gray-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-1.5">Pet Accommodations</label>
                      <input
                        type="text"
                        value={petAccommodations}
                        onChange={(e) => setPetAccommodations(e.target.value)}
                        placeholder="Enter preferences..."
                        className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 outline-none focus:border-primary-400 transition-colors placeholder-gray-400"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>


        {/* Footer */}
        <div className={`${asModal ? 'px-5 py-4' : 'px-6 py-4'} border-t border-gray-100 shrink-0`}>
          {editingPassenger ? (
            <div className="flex items-center gap-3 w-full">
              <button
                onClick={async () => { setRemoving(true); try { await onRemove(); } finally { setRemoving(false); } }}
                disabled={removing}
                className={`flex-1 flex items-center justify-center gap-2 py-3 border border-red-300 text-sm rounded-full transition-colors ${removing ? 'opacity-50 cursor-not-allowed text-red-300' : 'text-red-500 hover:bg-red-50'}`}
              >
                {removing ? (
                  <div className="w-4 h-4 border-2 border-red-300 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <RiDeleteBinLine className="w-4 h-4" />
                )}
                {removing ? 'Removing...' : 'Remove'}
              </button>
              <button
                onClick={handleSubmit}
                disabled={!isFormValid || saving}
                className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm rounded-full transition-colors ${
                  isFormValid && !saving
                    ? "bg-primary-700 text-white hover:bg-gray-800"
                    : "bg-gray-200 text-gray-400 cursor-not-allowed"
                }`}
              >
                {saving ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <RiCheckLine className="w-4 h-4" />
                )}
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={!isFormValid || saving}
              className={`w-full flex items-center justify-center gap-2 py-3 text-sm rounded-full transition-colors ${
                isFormValid && !saving
                  ? "bg-primary-500 text-white hover:bg-primary-600"
                  : "bg-gray-200 text-gray-400 cursor-not-allowed"
              }`}
            >
              {saving ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <RiUserAddLine className="w-4 h-4" />
              )}
              {saving ? 'Adding...' : 'Add Passenger'}
            </button>
          )}
        </div>
    </>
  );

  const selectPopup = (
    <SelectPassengerPopup
      isOpen={selectPopupOpen}
      onClose={() => setSelectPopupOpen(false)}
      passengers={clients}
      onSelect={(p) => {
        setFullName(p.full_name || [p.first_name, p.last_name].filter(Boolean).join(" "));
        const rawDob = p.date_of_birth || "";
        const dobMatch = rawDob.match(/^(\d{4})-(\d{2})-(\d{2})/);
        setDateOfBirth(dobMatch ? `${dobMatch[2]}/${dobMatch[3]}/${dobMatch[1]}` : rawDob);
        setNationality(p.nationality || "");
        setExistingPassportUrl(p.passport_url || "");
        setExistingVisaUrl(p.visa_url || "");
        setClientPassengerId(p.client_passenger_id || null);
        setPassportFiles([]);
        setVisaFiles([]);
        setWeight(p.weight || "");
        const info = p.additional_info || {};
        setDietaryCatering(info.dietary_catering || "");
        setCabinEnvironment(info.cabin_environment || "");
        setMedicalMobility(info.medical_mobility || "");
        setPetAccommodations(info.pet_accommodations || "");
        if (info.dietary_catering || info.cabin_environment || info.medical_mobility || info.pet_accommodations) {
          setAdditionalInfoOpen(true);
        }
        setSelectPopupOpen(false);
      }}
    />
  );

  if (asModal) {
    if (!isOpen) return null;
    return (
      <>
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={onClose} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[85vh] flex flex-col">
            {/* Modal header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 shrink-0">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingPassenger ? "Edit Passenger" : "Add Passenger"}
              </h2>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                <RiCloseLine className="w-5 h-5" />
              </button>
            </div>
            {formContent}
          </div>
        </div>
        {selectPopup}
      </>
    );
  }

  return (
    <Drawer open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DrawerContent
        overlayClassName="z-[1100]"
        // !h-[100dvh] clamps to the dynamic viewport so iOS Safari's bottom toolbar
        // doesn't cover the footer button (h-full would extend behind it).
        className="sm:max-w-lg !h-[100dvh] flex flex-col pb-[env(safe-area-inset-bottom)] z-[1100]"
      >
        <DrawerHeader className="shrink-0">
          <DrawerTitle>{editingPassenger ? "Edit Passenger" : "Add Passenger"}</DrawerTitle>
        </DrawerHeader>
        {formContent}
        {selectPopup}
      </DrawerContent>
    </Drawer>
  );
};

export default AddPassengerDrawer;
